/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Search bar, buyer pickers, and print action for ViewAppointments.
 * @module pages/viewAppointments/ViewAppointmentsSearchControls
 */

import Select from "react-select";
import styles from "../../styles/ViewAppointments.module.css";
import { ReactComponent as SearchIcon } from "../../assets/icons/BusinessEvents/SearchIcon.svg";
import { ReactComponent as FilterIcon } from "../../assets/icons/BusinessEvents/FilterIcon.svg";
import { ReactComponent as PrintIcon } from "../../assets/icons/BusinessEvents/PrintIcon.svg";
import { COMMON_SELECT_PLACEHOLDER_KEY } from "./viewAppointmentsConstants.js";
import {
  formatSelectOptionLabel,
  getSelectOptionValue,
} from "./viewAppointmentsSelectUtils.jsx";

const ViewAppointmentsSearchControls = ({
  t,
  companyNameCollator,
  searchQuery,
  userData,
  buyerCompanies,
  selectedBuyerCompany,
  buyerExecutives,
  selectedBuyerExecutive,
  requirements,
  selectedRequirement,
  primarySelectStyles,
  executiveSelectStyles,
  requirementSelectStyles,
  onSearch,
  onBuyerCompanyChange,
  onBuyerExecutiveChange,
  onRequirementChange,
  onToggleFiltersDropdown,
  onPrintCalendar,
}) => (
  <div className={styles.searchSection}>
    <div className={styles.searchControlsContainer}>
      <div className={styles.leftControlsGroup}>
        <div className={styles.searchInputContainer}>
          <SearchIcon className={styles.searchIcon} />
          <input
            type="text"
            className={styles.searchInput}
            placeholder={t("pages.viewAppointments.searchPlaceholder")}
            value={searchQuery}
            onChange={onSearch}
          />
        </div>

        {userData?.tipoDeUsuario === "5" && (
          <div className={styles.buyerCompanySelectContainer}>
            <label className={styles.buyerCompanyLabel}>
              {t("pages.viewAppointments.company")}
            </label>
            <Select
              options={[...buyerCompanies]
                .sort((left, right) =>
                  companyNameCollator.compare(
                    left.razonSocial || "",
                    right.razonSocial || "",
                  ),
                )
                .map((company) => ({
                  value: company._id,
                  label: company.razonSocial || t("common.unnamedCompany"),
                }))}
              isSearchable={false}
              isClearable
              value={selectedBuyerCompany}
              onChange={onBuyerCompanyChange}
              placeholder={t(COMMON_SELECT_PLACEHOLDER_KEY)}
              menuPortalTarget={document.body}
              styles={primarySelectStyles}
              getOptionValue={getSelectOptionValue}
              formatOptionLabel={formatSelectOptionLabel}
            />
          </div>
        )}

        {userData?.tipoDeUsuario === "5" && (
          <div className={styles.buyerCompanySelectContainer}>
            <label className={styles.buyerCompanyLabel}>
              {t("pages.viewAppointments.buyer")}
            </label>
            <Select
              options={buyerExecutives.map((buyer) => ({
                value: buyer._id,
                label: buyer.nombre || t("common.unnamedBuyer"),
              }))}
              isSearchable={false}
              isClearable
              isDisabled={!selectedBuyerCompany}
              value={selectedBuyerExecutive}
              onChange={onBuyerExecutiveChange}
              placeholder={t(COMMON_SELECT_PLACEHOLDER_KEY)}
              menuPortalTarget={document.body}
              styles={executiveSelectStyles}
              getOptionValue={getSelectOptionValue}
              formatOptionLabel={formatSelectOptionLabel}
            />
          </div>
        )}

        <div className={styles.requirementSelectContainer}>
          <label className={styles.requirementLabel}>
            {t("pages.viewAppointments.requirement")}
          </label>
          <div className={styles.requirementControlsContainer}>
            <Select
              options={requirements.map((req) => ({
                value: req._id,
                label: req.nombre || t("common.unnamedRequirement"),
              }))}
              isSearchable={false}
              isClearable
              isDisabled={
                userData?.tipoDeUsuario === "5" && !selectedBuyerExecutive
              }
              value={selectedRequirement}
              onChange={onRequirementChange}
              placeholder={t(COMMON_SELECT_PLACEHOLDER_KEY)}
              menuPortalTarget={document.body}
              styles={requirementSelectStyles}
              getOptionValue={getSelectOptionValue}
              formatOptionLabel={formatSelectOptionLabel}
            />
            <button
              className={styles.settingsButton}
              onClick={onToggleFiltersDropdown}
            >
              <FilterIcon />
            </button>
          </div>
        </div>
      </div>

      <div className={styles.printButtonContainer}>
        <button className={styles.printButton} onClick={onPrintCalendar}>
          <PrintIcon className={styles.printIcon} />
          <span className={styles.printButtonText}>
            {t("pages.viewAppointments.printCalendar")}
          </span>
        </button>
      </div>
    </div>
  </div>
);

export default ViewAppointmentsSearchControls;
